import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Phone as PhoneIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useAuth } from '../services/AuthService';
import { usePostHog } from '@posthog/react';
import { getSafeRedirectUrl } from '../utils/redirectUtils';
import {
  buildLoginUrl,
  buildVerifyUrl,
  formatPhoneNumber,
  persistVerifyFlowContext,
} from '../utils/authFlowUtils';

const SignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = getSafeRedirectUrl(searchParams.get('returnTo'));
  const { signUp, isAuthenticated } = useAuth();
  const posthog = usePostHog();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [existingAccountPhone, setExistingAccountPhone] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, navigate, returnTo]);

  const validateForm = () => {
    if (!firstName.trim()) return 'First name is required';
    if (!lastName.trim()) return 'Last name is required';
    if (!phone.trim()) return 'Phone number is required';
    
    const formattedPhone = formatPhoneNumber(phone);
    if (!formattedPhone || formattedPhone.length < 10) {
      return 'Please enter a valid phone number';
    }
    
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(password)) return 'Password must contain an uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain a lowercase letter';
    if (!/[0-9]/.test(password)) return 'Password must contain a number';
    if (password !== confirmPassword) return 'Passwords do not match';
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setExistingAccountPhone('');
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const formattedPhone = formatPhoneNumber(phone);
      await signUp(formattedPhone, password, firstName.trim(), lastName.trim());
      setExistingAccountPhone('');

      posthog?.capture('user_signed_up', {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });

      persistVerifyFlowContext({
        phone: formattedPhone,
        returnTo,
        next: 'signup',
      });
      navigate(buildVerifyUrl({ phone: formattedPhone, returnTo }));
    } catch (err) {
      if (err.name === 'UsernameExistsException') {
        const formattedPhone = formatPhoneNumber(phone);
        setExistingAccountPhone(formattedPhone);
        setError('We could not create the account with that phone number. Sign in to continue. If the account still needs verification, we will take you there after sign-in. If you do not remember your password, use Forgot password from the sign-in page.');
      } else if (err.name === 'InvalidPasswordException') {
        setExistingAccountPhone('');
        setError('Password does not meet requirements. Use at least 8 characters with uppercase, lowercase, and numbers.');
      } else if (err.name === 'InvalidParameterException') {
        setExistingAccountPhone('');
        setError('Please check your phone number format (e.g., 04XX XXX XXX).');
      } else {
        setExistingAccountPhone('');
        setError(err.message || 'Sign up failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            Create Account
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Join Labourando today
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {existingAccountPhone && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Button
              component={RouterLink}
              to={buildLoginUrl({ phone: existingAccountPhone, returnTo })}
              variant="outlined"
            >
              Sign In to Continue
            </Button>
          </Box>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
            <TextField
              fullWidth
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                },
              }}
              autoComplete="given-name"
              autoFocus
            />
            <TextField
              fullWidth
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
            />
          </Box>

          <TextField
            fullWidth
            label="Phone Number"
            placeholder="04XX XXX XXX"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setExistingAccountPhone('');
            }}
            sx={{ mb: 2.5 }}
            helperText="This will be your login username. Australian format."
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              },
            }}
            autoComplete="tel"
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 2.5 }}
            helperText="At least 8 characters with uppercase, lowercase, and numbers"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            autoComplete="new-password"
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            sx={{ mb: 3 }}
            error={confirmPassword.length > 0 && password !== confirmPassword}
            helperText={
              confirmPassword.length > 0 && password !== confirmPassword
                ? 'Passwords do not match'
                : ''
            }
            autoComplete="new-password"
          />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            By creating an account you agree to Labourando's{' '}
            <Link href="/terms" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 'bold' }}>Terms & Conditions</Link>{' '}
            and{' '}
            <Link href="/privacy" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 'bold' }}>Privacy Policy</Link>.
            You consent to the collection and sharing of profile information and uploaded documents as described in the Privacy Policy.
          </Typography>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || !firstName || !lastName || !phone || !password || !confirmPassword}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
              py: 1.5,
              fontWeight: 'bold',
              mb: 3,
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link
                component={RouterLink}
                to={`/login${returnTo !== '/' ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`}
                sx={{ color: 'primary.main', fontWeight: 'bold' }}
              >
                Sign In
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default SignupPage;
